import api from "../../../services/apiClient";
import { normalizeFolderCategory } from "./isfFolderLibraryService";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { getUserData } from "../../../services/apiService";
import { isRoleGuid, resolveRecipientRoleIds } from "../../../services/lookupService";

const resolveSessionUser = (userData) => {
  if (userData?.OrganizationId && userData?.StaffId) return userData;
  const session = getUserData();
  if (session?.OrganizationId) return session;
  return userData || session || {};
};

const basePayload = (userData, overrides = {}) => {
  const session = resolveSessionUser(userData);
  return {
    OrganizationIdRef: session.OrganizationId || session.organizationId || "",
    CreatedBy: session.StaffId || session.staffId || "",
    ActionType: "",
    ...overrides,
  };
};

function extractDataTableRows(response, tableName) {
  const data = response?.data;
  if (!data) return [];

  const tryArray = (value) => (Array.isArray(value) ? value : null);
  const target = String(tableName || "").toLowerCase();
  const secondaryTargets = new Set([
    "milestoneplanitems",
    "milestonenotifications",
    "milestonerecords",
  ]);

  if (Array.isArray(data)) return data;

  if (typeof data === "object") {
    for (const key of Object.keys(data)) {
      if (key === "Tables") continue;
      if (String(key).toLowerCase() === target) {
        const rows = tryArray(data[key]);
        if (rows) return rows;
      }
    }

    const direct =
      tryArray(data[tableName]) ??
      tryArray(data[tableName?.toLowerCase?.()]) ??
      tryArray(data[tableName?.toUpperCase?.()]);
    if (direct) return direct;
  }

  if (Array.isArray(data?.Tables)) {
    const named = data.Tables.find(
      (t) =>
        String(t?.TableName || t?.Name || t?.tableName || "").toLowerCase() === target,
    );
    if (Array.isArray(named?.Rows)) return named.Rows;

    if (secondaryTargets.has(target) && data.Tables.length > 1) {
      const second = data.Tables[1]?.Rows;
      if (Array.isArray(second)) return second;
    }

    if (data.Tables.length === 1) {
      const first = data.Tables[0]?.Rows;
      if (Array.isArray(first)) return first;
    }

    if (!secondaryTargets.has(target)) {
      const first = data.Tables[0]?.Rows;
      if (Array.isArray(first)) return first;
    }
  }

  if (secondaryTargets.has(target)) {
    return tryArray(data.Table1) ?? tryArray(data.table1) ?? [];
  }

  return tryArray(data.Table) ?? tryArray(data.table) ?? [];
}

export const normalizeMilestoneList = (response) =>
  extractDataTableRows(response, "MilestoneList");

/** True when API succeeded but returned no result tables (usually a swallowed SQL/SP failure). */
export const isMilestoneListResponseBroken = (response) => {
  if (response?.success !== 1 && response?.success !== true && response?.success !== "1") {
    return false;
  }
  const rows = normalizeMilestoneList(response);
  if (rows.length) return false;

  const data = response?.data;
  if (!data) return true;
  if (Array.isArray(data)) return false;

  const tableKeys = [
    "MilestoneList",
    "milestoneList",
    "Table",
    "table",
    "Table1",
    "table1",
  ];
  if (tableKeys.some((key) => Array.isArray(data[key]))) return false;

  if (Array.isArray(data.Tables)) {
    return !data.Tables.some((table) => Array.isArray(table?.Rows));
  }

  if (data.Tables && typeof data.Tables === "object") {
    return !Object.values(data.Tables).some((tableRows) => Array.isArray(tableRows));
  }

  return true;
};

/** True when plan lookup API succeeded but returned no usable table (SP/deploy issue). */
export const isMilestonePlanLookupBroken = (response) => {
  if (response?.success !== 1 && response?.success !== true && response?.success !== "1") {
    return false;
  }
  const rows = normalizeMilestonePlanLookup(response);
  if (rows.length) return false;

  const data = response?.data;
  if (!data) return true;
  if (Array.isArray(data)) return false;

  const tableKeys = [
    "MilestonePlanLookup",
    "milestonePlanLookup",
    "Table",
    "table",
    "Table1",
    "table1",
  ];
  if (tableKeys.some((key) => Array.isArray(data[key]))) return false;

  if (Array.isArray(data.Tables)) {
    return !data.Tables.some((table) => Array.isArray(table?.Rows));
  }

  if (data.Tables && typeof data.Tables === "object") {
    return !Object.values(data.Tables).some((tableRows) => Array.isArray(tableRows));
  }

  return true;
};

export const normalizeMilestoneLookup = (response) => {
  const named = extractDataTableRows(response, "MilestoneLookup");
  if (named.length) return named;
  const fromList = extractDataTableRows(response, "MilestoneList").filter((row) => {
    const status = String(row?.Status ?? row?.status ?? "Published").trim().toLowerCase();
    return status === "published";
  });
  if (fromList.length) return fromList;
  return extractAllRowsFromMilestoneData(response?.data).filter(
    (row) => row?.MilestoneId || row?.milestoneId,
  );
};

export const mapMilestoneLookupOptions = (list = []) =>
  (list || [])
    .map((m) => ({
      value: String(m.MilestoneId ?? m.milestoneId ?? "").trim(),
      label: `${m.MilestoneCode ?? m.milestoneCode ?? ""} — ${m.MilestoneName ?? m.milestoneName ?? ""}`.trim(),
      milestoneCode: m.MilestoneCode ?? m.milestoneCode ?? "",
      milestoneName: m.MilestoneName ?? m.milestoneName ?? "",
    }))
    .filter((opt) => opt.value);

export const pickLatestPublishedPlanRows = (list = []) => {
  const byRoot = new Map();

  (list || []).forEach((row) => {
    const planId = String(row.MilestonePlanId ?? row.milestonePlanId ?? "").trim();
    if (!planId) return;

    const rootId = String(
      row.RootMilestonePlanId ?? row.rootMilestonePlanId ?? planId,
    ).trim();
    const existing = byRoot.get(rootId);
    const rowTime = new Date(row.SystemDateTime ?? row.systemDateTime ?? 0).getTime();
    const existingTime = existing
      ? new Date(existing.SystemDateTime ?? existing.systemDateTime ?? 0).getTime()
      : -1;

    if (!existing || rowTime > existingTime) {
      byRoot.set(rootId, row);
      return;
    }

    if (rowTime === existingTime) {
      const rowVersion = String(row.VersionNo ?? row.versionNo ?? "");
      const existingVersion = String(existing.VersionNo ?? existing.versionNo ?? "");
      if (rowVersion.localeCompare(existingVersion, undefined, { numeric: true }) > 0) {
        byRoot.set(rootId, row);
      }
    }
  });

  return Array.from(byRoot.values());
};

export const mapMilestonePlanLookupOptions = (list = []) =>
  pickLatestPublishedPlanRows(list)
    .map((p) => {
      const planName = p.PlanName ?? p.planName ?? "Plan";
      const version = String(p.VersionNo ?? p.versionNo ?? "").trim();
      const count = Number(p.MilestoneCount ?? p.milestoneCount) || 0;
      const versionSuffix = version ? ` (${version})` : "";
      const countSuffix = count > 0 ? ` (${count} milestones)` : "";

      return {
        value: String(p.MilestonePlanId ?? p.milestonePlanId ?? "").trim(),
        label: `${planName}${countSuffix}${versionSuffix}`,
        planName,
        versionNo: version,
      };
    })
    .filter((opt) => opt.value);

export const resolveMilestonePlanOption = (planId, planName, options = []) => {
  const id = String(planId ?? "").trim();
  if (!id) return null;
  const match = options.find(
    (opt) => String(opt.value).trim().toLowerCase() === id.toLowerCase(),
  );
  if (match) return match;
  return {
    value: id,
    label: planName || "Milestone plan",
    planName: planName || "",
  };
};

export const normalizeMilestoneDetail = (response) => {
  const detail = extractDataTableRows(response, "MilestoneDetail")[0] || null;
  const notifications = extractDataTableRows(response, "MilestoneNotifications");
  const records = extractDataTableRows(response, "MilestoneRecords");
  return { detail, notifications, records };
};

export const normalizeMilestoneVersionHistory = (response) =>
  extractDataTableRows(response, "MilestoneVersionHistory");

export const normalizeMilestoneUsagePlans = (response) =>
  extractDataTableRows(response, "MilestoneUsagePlans");

export const normalizeMilestoneUsageTemplates = (response) =>
  extractDataTableRows(response, "MilestoneUsageTemplates");

export const normalizeMilestoneUsageStudies = (response) =>
  extractDataTableRows(response, "MilestoneUsageStudies");

export const isMilestoneInUse = (row) =>
  Boolean(
    row?.IsSystemSource === true ||
      row?.IsSystemSource === 1 ||
      Number(row?.UsedInPlans) > 0 ||
      Number(row?.UsedOnTemplates) > 0 ||
      Number(row?.UsedInStudies) > 0,
  );

export const fetchMilestoneUsageStudies = ({ userData, milestoneId }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, {
      ActionType: "GetMilestoneUsageStudies",
      MilestoneId: milestoneId,
    }),
  );

export const normalizeMilestonePlanList = (response) =>
  extractDataTableRows(response, "MilestonePlanList");

export const normalizeMilestonePlanLookup = (response) =>
  extractDataTableRows(response, "MilestonePlanLookup");

export const normalizeMilestonePlanDetail = (response) => {
  const detail = extractDataTableRows(response, "MilestonePlanDetail")[0] || null;
  const items = extractDataTableRows(response, "MilestonePlanItems");
  return { detail, items };
};

export const normalizeMilestonePlanUsageTemplates = (response) =>
  extractDataTableRows(response, "MilestonePlanUsageTemplates");

export const normalizeMilestonePlanItems = (response) =>
  extractMilestonePlanItemsFromResponse(response);

function extractAllRowsFromMilestoneData(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;

  const buckets = [];
  const collect = (value) => {
    if (Array.isArray(value) && value.length && typeof value[0] === "object") {
      buckets.push(value);
    }
  };

  if (Array.isArray(data.Tables)) {
    data.Tables.forEach((table) => collect(table?.Rows));
  }

  Object.keys(data).forEach((key) => {
    if (key === "Tables") return;
    collect(data[key]);
  });

  collect(data.Table);
  collect(data.Table1);
  collect(data.table);
  collect(data.table1);

  if (!buckets.length) return [];

  const itemsTable = buckets.find(
    (rows) => rows[0]?.MilestoneCode || rows[0]?.milestoneCode || rows[0]?.PlanItemId,
  );
  if (itemsTable) return itemsTable;

  return buckets.reduce((largest, rows) => (rows.length > largest.length ? rows : largest), buckets[0]);
}

export function extractMilestonePlanItemsFromResponse(response) {
  const named = extractDataTableRows(response, "MilestonePlanItems");
  if (named.length) return named;

  const { items } = normalizeMilestonePlanDetail(response);
  if (items?.length) return items;

  return extractAllRowsFromMilestoneData(response?.data);
}

export const isMilestoneApiSuccess = (response) =>
  response?.success === 1 || response?.success === true || response?.success === "1";

/** Load plan milestones for list popup (uses GetMilestonePlanById — same as View). */
export const fetchPlanMilestoneItemsForPopup = async ({ userData, milestonePlanId }) => {
  const session = resolveSessionUser(userData);
  if (!session?.OrganizationId && !session?.organizationId) {
    return {
      ok: false,
      items: [],
      message: "Session expired. Please refresh the page or log in again.",
      response: null,
    };
  }

  const response = await fetchMilestonePlanById({ userData: session, milestonePlanId });
  if (!isMilestoneApiSuccess(response)) {
    return {
      ok: false,
      items: [],
      message: extractMilestoneApiMessage(response, "Failed to load milestones."),
      response,
    };
  }

  return {
    ok: true,
    items: extractMilestonePlanItemsFromResponse(response),
    message: "",
    response,
  };
};

export const normalizeTemplateMilestonePlan = (response) =>
  extractDataTableRows(response, "TemplateMilestonePlan")[0] || null;

export const fetchMilestoneList = ({ userData }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, { ActionType: "GetMilestoneList" }),
  );

export const fetchMilestoneById = ({ userData, milestoneId }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, { ActionType: "GetMilestoneById", MilestoneId: milestoneId }),
  );

export const fetchMilestoneLookup = ({ userData }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, { ActionType: "GetMilestoneLookup" }),
  );

export const fetchMilestoneUsagePlans = ({ userData, milestoneId }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, {
      ActionType: "GetMilestoneUsagePlans",
      MilestoneId: milestoneId,
    }),
  );

export const fetchMilestoneUsageTemplates = ({ userData, milestoneId }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, {
      ActionType: "GetMilestoneUsageTemplates",
      MilestoneId: milestoneId,
    }),
  );

export const saveMilestone = ({ userData, payload }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, { ActionType: "SaveMilestone", ...payload }),
  );

export const updateMilestone = ({ userData, payload }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, { ActionType: "UpdateMilestone", ...payload }),
  );

export const publishMilestone = ({ userData, milestoneId, reason }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, {
      ActionType: "PublishMilestone",
      MilestoneId: milestoneId,
      Reason: reason || "",
    }),
  );

export const createMilestoneDraftVersion = ({ userData, milestoneId }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, {
      ActionType: "CreateMilestoneDraftVersion",
      MilestoneId: milestoneId,
    }),
  );

export const fetchMilestoneVersionHistory = ({ userData, milestoneId }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, {
      ActionType: "GetMilestoneVersionHistory",
      MilestoneId: milestoneId,
    }),
  );

export const extractMilestoneActionResult = (response) => {
  const row =
    response?.data?.SuccessStory?.[0] ??
    response?.data?.successStory?.[0] ??
    extractDataTableRows(response, "SuccessStory")[0] ??
    null;
  return row;
};

export const DUPLICATE_MILESTONE_CODE_ERROR = "Duplicate code.";

export const extractMilestoneApiMessage = (response, fallback = "Request failed.") => {
  const row =
    extractMilestoneActionResult(response) ||
    extractDataTableRows(response, "Table")[0] ||
    extractDataTableRows(response, "SuccessStory")[0] ||
    null;
  const tableMsg = String(row?.MSG ?? row?.msg ?? row?.Message ?? row?.message ?? "").trim();
  const topMsg = String(response?.message || response?.Message || "").trim();
  return tableMsg || topMsg || fallback;
};

export const isDuplicateMilestoneCodeMessage = (message) => {
  const msg = String(message || "").trim();
  return (
    /duplicate code/i.test(msg) ||
    /code already exists/i.test(msg) ||
    /milestone with this code already exists/i.test(msg)
  );
};

export const resolveMilestoneDuplicateCodeError = (response) => {
  if (isDuplicateMilestoneCodeMessage(extractMilestoneApiMessage(response, ""))) {
    return DUPLICATE_MILESTONE_CODE_ERROR;
  }
  return null;
};

export const deleteMilestone = ({ userData, milestoneId, reason }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, {
      ActionType: "DeleteMilestone",
      MilestoneId: milestoneId,
      Reason: reason || "",
    }),
  );

export const fetchMilestonePlanList = ({ userData }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, { ActionType: "GetMilestonePlanList" }),
  );

export const fetchMilestonePlanById = ({ userData, milestonePlanId }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, { ActionType: "GetMilestonePlanById", MilestonePlanId: milestonePlanId }),
  );

export const fetchMilestonePlanUsageTemplates = ({ userData, milestonePlanId }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, {
      ActionType: "GetMilestonePlanUsageTemplates",
      MilestonePlanId: milestonePlanId,
    }),
  );

export const fetchMilestonePlanItems = ({ userData, milestonePlanId }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, {
      ActionType: "GetMilestonePlanItems",
      MilestonePlanId: milestonePlanId,
    }),
  );

export const fetchMilestonePlanLookup = ({ userData }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, { ActionType: "GetMilestonePlanLookup" }),
  );

export const saveMilestonePlan = ({ userData, payload }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, { ActionType: "SaveMilestonePlan", ...payload }),
  );

export const updateMilestonePlan = ({ userData, payload }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, { ActionType: "UpdateMilestonePlan", ...payload }),
  );

export const publishMilestonePlan = ({ userData, milestonePlanId, reason }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, {
      ActionType: "PublishMilestonePlan",
      MilestonePlanId: milestonePlanId,
      Reason: reason || "",
    }),
  );

export const createMilestonePlanDraftVersion = ({ userData, milestonePlanId }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, {
      ActionType: "CreateMilestonePlanDraftVersion",
      MilestonePlanId: milestonePlanId,
    }),
  );

export const extractMilestonePlanActionResult = (response) => {
  const row =
    response?.data?.SuccessStory?.[0] ??
    response?.data?.successStory?.[0] ??
    extractDataTableRows(response, "SuccessStory")[0] ??
    null;
  return row;
};

export const deleteMilestonePlan = ({ userData, milestonePlanId, reason }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, {
      ActionType: "DeleteMilestonePlan",
      MilestonePlanId: milestonePlanId,
      Reason: reason || "",
    }),
  );

export const fetchTemplateMilestonePlan = ({ userData, templateId }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, { ActionType: "GetTemplateMilestonePlan", TemplateId: templateId }),
  );

export const applyMilestonePlanToTemplate = ({ userData, templateId, milestonePlanId }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, {
      ActionType: "ApplyMilestonePlanToTemplate",
      TemplateId: templateId,
      MilestonePlanId: milestonePlanId,
    }),
  );

export const previewMilestonePlanMapping = ({ userData, templateId, milestonePlanId }) =>
  api.post(
    API_ENDPOINTS.ISF.MILESTONE_ACTIVITY,
    basePayload(userData, {
      ActionType: "PreviewMilestonePlanMapping",
      TemplateId: templateId || null,
      MilestonePlanId: milestonePlanId,
    }),
  );

export const normalizeMilestonePlanMappingPreview = (response) => {
  const named = extractDataTableRows(response, "MilestonePlanMappingPreview");
  if (named.length) return named;
  const fallback = extractDataTableRows(response, "Table1");
  if (fallback.length) return fallback;
  return extractDataTableRows(response).filter(
    (row) => row?.MilestoneCode || row?.milestoneCode,
  );
};

export const normalizeMilestonePlanApplyResult = (response) => {
  const summary =
    extractDataTableRows(response, "ApplyMilestonePlanSummary")[0] ||
    extractDataTableRows(response, "SuccessStory")[0] ||
    null;
  const log = extractDataTableRows(response, "ApplyMilestonePlanLog").length
    ? extractDataTableRows(response, "ApplyMilestonePlanLog")
    : extractDataTableRows(response, "Table1");
  return { summary, log };
};

export const formatMilestoneListAnchorLabel = (row) => {
  const anchorType = String(row?.AnchorType ?? row?.anchorType ?? "").trim();
  const anchor = String(row?.Anchor ?? row?.anchor ?? "").trim();

  if (anchorType === "ReferenceDate" || anchor === "ReferenceDate") {
    return "Reference Date";
  }
  if (anchor === "AnotherMilestone") {
    return "Another Milestone";
  }
  if (anchor === "Reference Date" || anchor === "Another Milestone") {
    return anchor;
  }
  return anchor || "—";
};

export const formatMilestonePlanApplyMessage = (summary) => {
  if (!summary) return "";
  const placeholders = Number(summary.PlaceholdersCreated ?? 0);
  const folders = Number(summary.FoldersCreated ?? 0);
  const skipped = Number(summary.MilestonesSkipped ?? 0);
  const parts = [`${placeholders} expected document(s)`];
  if (folders > 0) parts.push(`${folders} folder(s) created`);
  if (skipped > 0) parts.push(`${skipped} milestone(s) skipped (missing default folder)`);
  return `Milestone plan applied: ${parts.join(", ")}.`;
};

export const summarizeMilestonePlanMappingPreview = (rows = []) => {
  const list = Array.isArray(rows) ? rows : [];
  return {
    total: list.length,
    willCreate: list.filter((r) => r.FolderAction === "WillCreate").length,
    existing: list.filter((r) => r.FolderAction === "Existing").length,
    missing: list.filter((r) => r.FolderAction === "MissingDefaultFolder").length,
    placeholders: list.reduce((sum, r) => sum + Number(r.PlaceholderCount || 0), 0),
  };
};

export const MILESTONE_CATEGORIES = [
  "Clinical",
  "Regulatory",
  "Safety",
  "Quality",
  "Administrative",
];

const MILESTONE_CATEGORY_SLUG = {
  Clinical: "clinical",
  Regulatory: "regulatory",
  Safety: "safety",
  Quality: "quality",
  Administrative: "administrative",
};

export const milestoneCategoryClassName = (category) => {
  const slug =
    MILESTONE_CATEGORY_SLUG[category] ||
    String(category || "default")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-") ||
    "default";
  return `milestone-category-badge milestone-category-badge--${slug}`;
};

export const ANCHOR_TYPES = [
  { value: "ReferenceDate", label: "Reference Date" },
  { value: "AnotherMilestone", label: "Another Milestone" },
];

export const NOTIFICATION_ROLE_FALLBACK = [];

export const NOTIFICATION_CHANNEL = "Email";

const escapeXml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

export const prepareMilestoneNotifications = (rules = []) =>
  (rules || []).map((rule, index) => ({
    TriggerCode: rule.TriggerCode || "",
    TriggerLabel: rule.TriggerLabel || "",
    DefaultRecipients: String(rule.DefaultRecipients || "").trim(),
    Channel: NOTIFICATION_CHANNEL,
    SortOrder: index,
    IsEnabled: rule.IsEnabled !== false,
  }));

export const buildMilestoneNotificationXml = (rules = []) => {
  const items = prepareMilestoneNotifications(rules).filter((rule) =>
    String(rule.TriggerCode || "").trim(),
  );
  if (!items.length) return "";

  return `<ArrayOfNotification>${items
    .map(
      (rule) =>
        `<Notification>` +
        `<TriggerCode>${escapeXml(rule.TriggerCode)}</TriggerCode>` +
        `<TriggerLabel>${escapeXml(rule.TriggerLabel)}</TriggerLabel>` +
        `<DefaultRecipients>${escapeXml(rule.DefaultRecipients)}</DefaultRecipients>` +
        `<Channel>${escapeXml(rule.Channel)}</Channel>` +
        `<IsEnabled>${rule.IsEnabled ? 1 : 0}</IsEnabled>` +
        `<SortOrder>${rule.SortOrder}</SortOrder>` +
        `</Notification>`,
    )
    .join("")}</ArrayOfNotification>`;
};

export const prepareMilestoneRecords = (records = []) =>
  (records || [])
    .map((row, index) => ({
      RecordName: String(row.RecordName || row.recordName || "").trim(),
      RecordType: row.RecordType || row.recordType || "Triggering",
      SortOrder: row.SortOrder ?? index,
    }))
    .filter((row) => row.RecordName);

export const buildMilestoneRecordXml = (records = []) => {
  const items = prepareMilestoneRecords(records);
  if (!items.length) return "";

  return `<ArrayOfRecord>${items
    .map(
      (row) =>
        `<Record>` +
        `<RecordName>${escapeXml(row.RecordName)}</RecordName>` +
        `<RecordType>${escapeXml(row.RecordType)}</RecordType>` +
        `<SortOrder>${row.SortOrder}</SortOrder>` +
        `</Record>`,
    )
    .join("")}</ArrayOfRecord>`;
};

export const buildMilestoneSavePayload = (form) => ({
  MilestoneId: form.MilestoneId,
  MilestoneCode: form.MilestoneCode?.trim(),
  MilestoneName: form.MilestoneName?.trim(),
  Description: form.Description?.trim() || "",
  Category: form.Category,
  GateCode: form.GateCode || null,
  Tags: form.Tags?.trim() || "",
  DefaultFolderName: form.DefaultFolderName?.trim() || null,
  DefaultFolderCategory: normalizeFolderCategory(form.DefaultFolderCategory || "eISF"),
  DefaultUploaderRoles: form.DefaultUploaderRoles || "",
  DefaultSignerRoles: form.RequiresSignature ? form.DefaultSignerRoles || "" : "",
  RequiresSignature: !!form.RequiresSignature,
  AllowsNotApplicable: false,
  IsInformational: false,
  AnchorType: form.AnchorType,
  AnchorMilestoneCode: form.AnchorMilestoneCode || null,
  AnchorOffsetDays:
    form.AnchorOffsetDays === "" || form.AnchorOffsetDays == null
      ? null
      : Number(form.AnchorOffsetDays),
  Records: prepareMilestoneRecords(form.records),
  RecordXML: buildMilestoneRecordXml(form.records),
  Notifications: prepareMilestoneNotifications(form.notifications),
  NotificationXML: buildMilestoneNotificationXml(form.notifications),
});

export const validateMilestoneDefaultFolder = (folderName) => {
  if (!String(folderName || "").trim()) {
    return "Default folder is required.";
  }
  return null;
};

export const validateMilestoneAnchor = (anchorType, anchorMilestoneCode) => {
  if (anchorType !== "AnotherMilestone") return null;
  if (!String(anchorMilestoneCode || "").trim()) {
    return "Required.";
  }
  return null;
};

export const validateMilestoneUploaderRoles = (roleIdsCsv, roleOptions) =>
  validateMilestoneRoleSelection(roleIdsCsv, roleOptions, "uploader", true);

export const validateMilestoneRoleSelection = (
  roleIdsCsv,
  roleOptions,
  label,
  required = false,
) => {
  if (!required && !String(roleIdsCsv || "").trim()) return null;
  const ids = resolveRecipientRoleIds(roleIdsCsv, roleOptions).filter((id) =>
    isRoleGuid(id),
  );
  if (!ids.length) {
    return required
      ? `Select at least one ${label} role.`
      : null;
  }
  return null;
};

export const validateMilestoneNotifications = (rules = [], roleOptions = []) => {
  const messages = [];
  prepareMilestoneNotifications(rules).forEach((rule) => {
    if (!rule.IsEnabled) return;
    const recipientIds = resolveRecipientRoleIds(rule.DefaultRecipients, roleOptions).filter(
      (roleId) => isRoleGuid(roleId),
    );
    if (!recipientIds.length) {
      messages.push(
        `Select at least one recipient role for trigger ${rule.TriggerCode || ""}.`,
      );
    }
  });
  return messages;
};

export const DEFAULT_NOTIFICATION_RECIPIENT_ROLE_NAMES = [
  "System Administrator",
  "PI",
  "Principal Investigator",
];

export const resolveDefaultPiRoleId = (roleOptions = []) => {
  for (const name of ["PI", "Principal Investigator"]) {
    const match = roleOptions.find(
      (role) =>
        String(role?.roleName || "")
          .trim()
          .toLowerCase() === name.toLowerCase(),
    );
    if (match?.roleId) return match.roleId;
  }
  return null;
};

export const resolveDefaultNotificationRecipientIds = (roleOptions = []) => {
  const ids = [];
  const used = new Set();

  const pickRoleId = (names) => {
    for (const name of names) {
      const match = roleOptions.find(
        (role) =>
          String(role?.roleName || "")
            .trim()
            .toLowerCase() === name.toLowerCase(),
      );
      if (match?.roleId && !used.has(match.roleId)) {
        used.add(match.roleId);
        return match.roleId;
      }
    }
    return null;
  };

  const systemAdminId = pickRoleId(["System Administrator"]);
  const piId = pickRoleId(["PI", "Principal Investigator"]);

  if (systemAdminId) ids.push(systemAdminId);
  if (piId) ids.push(piId);

  return ids;
};

export const DEFAULT_NOTIFICATION_RULES = (defaultRecipientIds = []) => {
  const defaultRecipients = [...new Set((defaultRecipientIds || []).filter(isRoleGuid))].join(
    ",",
  );

  return [
  {
    TriggerCode: "T-7",
    TriggerLabel: "reminder",
    DefaultRecipients: defaultRecipients,
    Channel: NOTIFICATION_CHANNEL,
    IsEnabled: true,
    SortOrder: 0,
  },
  {
    TriggerCode: "T-0",
    TriggerLabel: "due",
    DefaultRecipients: defaultRecipients,
    Channel: NOTIFICATION_CHANNEL,
    IsEnabled: true,
    SortOrder: 1,
  },
  {
    TriggerCode: "T+1",
    TriggerLabel: "overdue",
    DefaultRecipients: defaultRecipients,
    Channel: NOTIFICATION_CHANNEL,
    IsEnabled: true,
    SortOrder: 2,
  },
  {
    TriggerCode: "T+7",
    TriggerLabel: "critical",
    DefaultRecipients: defaultRecipients,
    Channel: NOTIFICATION_CHANNEL,
    IsEnabled: true,
    SortOrder: 3,
  },
  ];
};

export const formatMilestoneDatePreview = ({
  anchorType = "ReferenceDate",
  anchorMilestoneCode,
  milestoneCode,
  offsetDays,
}) => {
  const offsetRaw =
    offsetDays === "" || offsetDays == null ? null : Number(String(offsetDays).trim());
  if (offsetRaw == null || Number.isNaN(offsetRaw)) return "";

  const code = (milestoneCode || "").trim() || "Milestone";
  const fmt = (d) =>
    d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

  const addDays = (base, days) => {
    const d = new Date(base);
    d.setDate(d.getDate() + days);
    return d;
  };

  const sampleReferenceDate = new Date();

  if (anchorType === "ReferenceDate") {
    const resultDate = addDays(sampleReferenceDate, offsetRaw);
    return `Ref ${fmt(sampleReferenceDate)} → ${code} ${fmt(resultDate)}`;
  }

  if (anchorType === "AnotherMilestone") {
    if (!anchorMilestoneCode) return "";
    // Illustrative sample: anchor milestone occurs 14 days after reference date.
    const sampleAnchorDate = addDays(sampleReferenceDate, 14);
    const resultDate = addDays(sampleAnchorDate, offsetRaw);
    return `${anchorMilestoneCode} ${fmt(sampleAnchorDate)} → ${code} ${fmt(resultDate)}`;
  }

  return "";
};

export const parseTags = (tags) =>
  String(tags || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

export const statusBadgeClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "published") return "milestone-status-badge milestone-status-badge--published";
  if (s === "draft") return "milestone-status-badge milestone-status-badge--draft";
  if (s === "superseded") return "milestone-status-badge milestone-status-badge--superseded";
  return "milestone-status-badge milestone-status-badge--default";
};

export function buildTemplatePlaceholdersFromMilestone({
  milestoneDetail,
  records = [],
  templateFolderId,
  sortOrderStart = 0,
}) {
  const milestoneId = String(milestoneDetail?.MilestoneId || milestoneDetail?.milestoneId || "").trim();
  const dueRaw = milestoneDetail?.AnchorOffsetDays ?? milestoneDetail?.anchorOffsetDays;
  const dueDays =
    dueRaw === "" || dueRaw == null || Number.isNaN(Number(dueRaw)) ? "" : Number(dueRaw);
  const uploaderRoles =
    milestoneDetail?.DefaultUploaderRoles ?? milestoneDetail?.defaultUploaderRoles ?? "";
  const signerRoles =
    milestoneDetail?.DefaultSignerRoles ?? milestoneDetail?.defaultSignerRoles ?? "";
  const requiresSignature =
    milestoneDetail?.RequiresSignature === true ||
    milestoneDetail?.RequiresSignature === 1 ||
    milestoneDetail?.RequiresSignature === "1";
  const description = milestoneDetail?.Description ?? milestoneDetail?.description ?? "";
  const milestoneCode = milestoneDetail?.MilestoneCode ?? milestoneDetail?.milestoneCode ?? "";
  const milestoneName = milestoneDetail?.MilestoneName ?? milestoneDetail?.milestoneName ?? "";

  const triggering = (records || []).filter(
    (row) => String(row?.RecordType || row?.recordType || "Triggering") === "Triggering",
  );

  const docRows = triggering.length
    ? triggering.map((row, index) => ({
        PlaceholderName: String(row.RecordName || row.recordName || "").trim(),
        SortOrder: sortOrderStart + Number(row.SortOrder ?? index),
      }))
    : [
        {
          PlaceholderName: milestoneName || milestoneCode || "Expected document",
          SortOrder: sortOrderStart,
        },
      ];

  return docRows
    .filter((row) => row.PlaceholderName)
    .map((row, index) => ({
      PlaceholderId: "",
      TemplateFolderId: templateFolderId,
      PlaceholderName: row.PlaceholderName,
      Description: description,
      IsRequired: true,
      SortOrder: row.SortOrder ?? sortOrderStart + index,
      DefaultDueDays: dueDays,
      DefaultAssigneeRole: uploaderRoles,
      RequiresSignature: requiresSignature,
      SignerRoles: requiresSignature ? signerRoles : "",
      AllowsNotApplicable: false,
      MilestoneId: milestoneId,
      MilestoneCode: milestoneCode,
      MilestoneName: milestoneName,
      _manualMilestone: true,
    }));
};
